import { useDealerDiscounts } from "app/hooks/checkout/useDealerDiscounts";
import {
  StandardProductFooterDataResultProps,
  useStandardProductFooterData,
} from "app/hooks/shared/standard-product/useStandardProductFooterData";
import React, { useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import InformationIcon from "shared/ui/icons/Information";
import { getFormattedPrice } from "shared/utils/getFormattedPrice";
import MonthlyPaymentModal from "widgets/shared/montly-payment-modal";
import { AmountSelector } from "./AmountSelector";

export interface StandardProductFooterProps {
  showExtraPriceText: boolean;
  monthlyPayment: number | undefined;
  isMonthlyPaymentAvailable: () => boolean;
  addStandardProductToCart: () => void; // Replace 'any' with the appropriate type for the product
}

export const StandardProductOptionsFooterNormal: React.FC<
  StandardProductFooterProps
> = ({ monthlyPayment, addStandardProductToCart }) => {
  const data = useStandardProductFooterData(monthlyPayment);

  const {
    isMonthlyPaymentModalOpened,
    setIsMonthlyPaymentModalOpened,
    showMonthlyPayment,
    standardProduct,
    standardProductName,
    standardProductPrice,
    changeAmount,
    amount,
  } = data as StandardProductFooterDataResultProps;

  // Dealer discounts, loading
  const { dealerDiscounts, isDealerDiscountsLoading } = useDealerDiscounts();

  const standardProductDiscounts = useMemo(() => {
    return dealerDiscounts?.find(
      (discount) => discount.productCode === standardProduct.productType.code,
    );
  }, [dealerDiscounts, standardProduct.productType.code]);

  const businessDiscount = standardProductDiscounts?.discount ?? 0;
  const businessDoorDiscountsPrice =
    standardProduct.price - (standardProduct.price * businessDiscount) / 100;

  const showDiscount =
    standardProductDiscounts &&
    standardProduct.price !== businessDoorDiscountsPrice;

  const savedAmount = (
    standardProduct.price - businessDoorDiscountsPrice
  ).toFixed(2);

  if (!data.standardProductName) {
    return null;
  }

  return (
    <>
      <MonthlyPaymentModal
        isOpen={isMonthlyPaymentModalOpened}
        setIsOpen={setIsMonthlyPaymentModalOpened}
      />
      <div
        className={`gg-grid gg-w-full gg-border-t-2 gg-border-gray-200 gg-bg-gray-50 ${
          !showDiscount ? "gg-h-24" : "gg-h-28"
        }`}
      >
        {/* First row */}
        <div className="gg-flex gg-w-full gg-flex-row gg-justify-between gg-px-8 gg-py-4 xl:gg-px-12 2xl:gg-px-16">
          {/* LEFT SIDE */}
          <div className="gg-flex gg-space-x-4">
            {/* Amount selector */}
            <div className="gg-flex">
              <AmountSelector changeAmount={changeAmount} quantity={amount} />
            </div>
            {/* Selected standard product name */}
            <h1 className="gg-self-center gg-pr-2 gg-text-lg gg-font-bold gg-text-slate-800 2xl:gg-text-xl">
              {standardProductName}
            </h1>
          </div>
          {/* LEFT SIDE END */}

          {/* RIGHT SIDE */}
          <div className="gg-flex gg-items-center gg-space-x-5 xl:gg-space-x-8">
            <div className="gg-flex gg-flex-col gg-space-y-1">
              <div className="gg-flex gg-flex-row">
                {!showDiscount ? (
                  <h1 className="gg-w-full gg-self-center gg-text-lg gg-font-bold gg-text-slate-800">
                    {standardProductPrice}
                  </h1>
                ) : (
                  <div className="gg-flex gg-flex-col">
                    {/* Price formatted output */}
                    <div className="gg-flex gg-items-center gg-gap-x-2">
                      <h4 className="gg-self-center gg-text-sm gg-font-bold gg-text-gray-400 gg-line-through">
                        {!isDealerDiscountsLoading ? (
                          standardProductPrice
                        ) : (
                          <Skeleton width={50} />
                        )}
                      </h4>
                    </div>
                    <h4 className="gg-text-lg gg-font-bold gg-text-red">
                      {!isDealerDiscountsLoading ? (
                        getFormattedPrice(businessDoorDiscountsPrice)
                      ) : (
                        <Skeleton width={90} />
                      )}
                    </h4>
                    <div className="gg-w-fit gg-bg-red gg-px-1">
                      <h4 className="gg-text-sm gg-font-bold gg-text-white">
                        {!isDealerDiscountsLoading ? (
                          <>
                            Save €{savedAmount} ({businessDiscount}%)
                          </>
                        ) : (
                          <Skeleton width={145} />
                        )}
                      </h4>
                    </div>
                  </div>
                )}

                {/* Monthly payment info icon */}
                {showMonthlyPayment && (
                  <button
                    className="gg-pl-0.5"
                    onClick={() =>
                      setIsMonthlyPaymentModalOpened(
                        !isMonthlyPaymentModalOpened,
                      )
                    }
                  >
                    <InformationIcon
                      className="gg-fill-gray-500 hover:gg-fill-gray-900"
                      width={28}
                      height={28}
                    />
                  </button>
                )}
              </div>

              {/* Monthly payment */}
              <h1
                className={`gg-text-sm gg-leading-5 gg-text-zinc-400 gg-transition-opacity gg-delay-150 gg-duration-300 gg-ease-in-out ${
                  showMonthlyPayment ? "gg-opacity-100" : "gg-opacity-0"
                }`}
              >
                Of v.a. €{monthlyPayment} p/m
              </h1>
            </div>
            {/* Buttons */}
            <div className="gg-flex gg-items-center">
              {/* add to cart */}

              <button
                type="button"
                onClick={() => addStandardProductToCart()}
                data-testid="add-to-cart-mobile"
                className="gg-justify-center gg-rounded-sm gg-bg-yellow-300 gg-px-4 gg-py-3.5 gg-text-base gg-font-bold gg-text-dark-blue hover:gg-bg-yellow-400 hover:gg-text-zinc-900 xl:gg-px-7 xl:gg-py-4 2xl:gg-px-9"
              >
                In winkelmand
              </button>
            </div>
          </div>
        </div>
        {/* First row end */}
      </div>
    </>
  );
};
